import { createContext, useContext, useEffect, useState } from "react";

import PropTypes from "prop-types";

import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import useLazyRestQuery from "@/remoteState/useLazyRestQuery";
import useUser from "@/users/useUser";

export const USER_ATTRIBUTES_SESSION_STORAGE = "userAttributes";
export const INVALIDATE_CACHE_TIMEOUT = 300000; // 5 mins

const UserAttributesQueryContext = createContext({ userAttributes: {} });

const useUserAttributes = () => useContext(UserAttributesQueryContext);

export const useFetchAttributes = () => {
  const [error, setError] = useState();
  const { data: user } = useUser();
  const [userAttributes, setUserAttributes] = useState();

  const [getUserAttributes] = useLazyRestQuery(
    `${MONOLITH_ORIGIN}/api/v3/user/attributes/`,
  );

  useEffect(() => {
    const fetchData = async () => {
      const cacheData = window.sessionStorage.getItem(
        USER_ATTRIBUTES_SESSION_STORAGE,
      );
      let cachedJson = {};
      try {
        cachedJson = cacheData && JSON.parse(cacheData);
      } catch (_e) {
        // Failed to parse cached JSON object, will proceed to make an API call and referesh the cache.
      }
      const cacheExpireTime = cacheData
        ? cachedJson?.expireTime
        : Number.MAX_SAFE_INTEGER;
      const dateNow = Date.now();
      const cacheExpired =
        (cacheData && dateNow > cacheExpireTime) || !cacheData;
      const isUserChanged =
        cachedJson?.data?.is_authenticated !== user?.is_authenticated;

      try {
        if (cacheExpired || isUserChanged) {
          const { data } = await getUserAttributes();
          setUserAttributes(data);
          const sessionData = {
            expireTime: Date.now() + INVALIDATE_CACHE_TIMEOUT,
            data,
          };
          window.sessionStorage.setItem(
            USER_ATTRIBUTES_SESSION_STORAGE,
            JSON.stringify(sessionData),
          );
        } else {
          setUserAttributes(JSON.parse(cacheData)?.data);
        }
      } catch (err) {
        setError(err);
      }
    };
    fetchData();
  }, [user]);

  return { userAttributes, error };
};

const UserAttributesProvider = ({ children }) => {
  const data = useFetchAttributes();
  return (
    <UserAttributesQueryContext.Provider value={data}>
      {children}
    </UserAttributesQueryContext.Provider>
  );
};

UserAttributesProvider.propTypes = {
  children: PropTypes.object,
};

export { UserAttributesProvider };

export default useUserAttributes;
